import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ShieldAlert } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { contactApi, apiError } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "" });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError("Podaj swoje imię lub nazwę firmy.");
    if (!EMAIL_RE.test(form.email.trim())) return setError("Podaj poprawny adres e-mail.");
    if (form.message.trim().length < 10) return setError("Wiadomość jest za krótka (min. 10 znaków).");
    if (!consent) return setError("Wymagana zgoda na przetwarzanie danych.");

    setLoading(true);
    try {
      await contactApi.send({
        name: form.name,
        email: form.email,
        message: form.message,
        consent,
        company: form.company, // honeypot
      });
      setSent(true);
      toast.success("Wiadomość wysłana.");
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" aria-label="Promptivo — strona główna">
            <Logo height={28} />
          </Link>
          <Link to="/pomoc" className="inline-flex items-center gap-1.5 text-sm text-brand-sec hover:text-brand-text">
            <ArrowLeft size={15} /> Powrót do pomocy
          </Link>
        </div>

        <div data-testid={CONTACT.root} className="rounded-panel border border-brand-line bg-brand-surface p-6 sm:p-8">
          {sent ? (
            <div data-testid={CONTACT.success} className="flex flex-col items-start gap-3 text-center sm:items-center">
              <CheckCircle2 className="text-brand-accent" size={40} />
              <h1 className="font-display text-2xl font-semibold text-brand-text">Dziękujemy, wiadomość dotarła</h1>
              <p className="text-brand-sec">
                Zapisaliśmy Twoje zgłoszenie ze statusem <strong className="text-brand-text">NOWA</strong>. Odpowiemy na
                podany adres e-mail najszybciej, jak to możliwe.
              </p>
              <Link
                to="/pomoc"
                className="mt-2 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"
              >
                Wróć do centrum pomocy
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Skontaktuj się z nami</h1>
              <p className="mt-2 text-brand-sec">Opisz, na którym etapie pojawił się problem — pomożemy.</p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
                {error && (
                  <div
                    data-testid={CONTACT.error}
                    className="rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                  >
                    {error}
                  </div>
                )}

                {/* Honeypot — hidden from users, catches bots */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.company}
                  onChange={set("company")}
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-brand-sec">Imię lub nazwa firmy</span>
                  <input
                    data-testid={CONTACT.nameInput}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jan Kowalski"
                    className="w-full rounded-field border border-brand-line bg-brand-bg px-4 py-3 text-[15px] text-brand-text placeholder:text-brand-muted outline-none focus:border-brand-accent"
                    required
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-brand-sec">E-mail</span>
                  <input
                    type="email"
                    data-testid={CONTACT.emailInput}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="ty@firma.pl"
                    className="w-full rounded-field border border-brand-line bg-brand-bg px-4 py-3 text-[15px] text-brand-text placeholder:text-brand-muted outline-none focus:border-brand-accent"
                    required
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-brand-sec">Wiadomość</span>
                  <textarea
                    data-testid={CONTACT.messageInput}
                    value={form.message}
                    onChange={set("message")}
                    rows={5}
                    placeholder="Opisz swój problem lub pytanie…"
                    className="w-full resize-y rounded-field border border-brand-line bg-brand-bg px-4 py-3 text-[15px] text-brand-text placeholder:text-brand-muted outline-none focus:border-brand-accent"
                    required
                  />
                </label>

                <label className="flex cursor-pointer items-start gap-3 text-sm text-brand-sec">
                  <input
                    type="checkbox"
                    data-testid={CONTACT.consentCheckbox}
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-brand-accent"
                  />
                  <span>
                    Wyrażam zgodę na przetwarzanie moich danych w celu obsługi zgłoszenia (
                    <Link to="/polityka-prywatnosci" className="text-brand-accent hover:underline">
                      polityka prywatności
                    </Link>
                    ).
                  </span>
                </label>

                <div className="flex items-start gap-2 rounded-field bg-brand-elevated px-3 py-2.5 text-xs text-brand-muted">
                  <ShieldAlert size={15} className="mt-0.5 shrink-0" />
                  <span>Dla bezpieczeństwa nigdy nie podawaj hasła, pełnych danych karty ani kodów autoryzacyjnych.</span>
                </div>

                <button
                  type="submit"
                  data-testid={CONTACT.submitButton}
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-button bg-brand-accent px-6 py-3 text-[15px] font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {loading ? "Wysyłanie…" : "Wyślij wiadomość"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
